import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Activity, 
  Zap, 
  Users, 
  Shield, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Copy,
  ExternalLink,
  Clock,
  DollarSign,
  Cpu,
  Globe,
  Database,
  Battery,
  Lightbulb,
  Wifi
} from 'lucide-react';
import { motion } from 'framer-motion';

interface NetworkStats {
  totalTransactions: number;
  tps: number;
  gasPrice: number;
  activeValidators: number;
  totalStaked: string;
  marketCap: string;
  vttnPrice: number;
  priceChange: number;
  totalSupply: string;
  circulatingSupply: string;
  energyStations: number;
  bssConnected: number;
  subnets: number;
  aiAgents: number;
  vtmPrice?: number;
  vtmChange?: number;
  transactions24h?: number;
  latestBlock?: number;
  blockTime?: number;
}

interface Block {
  number: number;
  hash: string;
  timestamp: string;
  transactions: number;
  gasUsed: number;
  gasLimit: number;
  validator: string;
  reward: string;
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  gasPrice: number;
  timestamp: string;
  status: 'success' | 'failed';
  type: 'transfer' | 'energy' | 'staking' | 'bridge';
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<NetworkStats>({
    totalTransactions: 0,
    tps: 0,
    gasPrice: 0,
    activeValidators: 0,
    totalStaked: '0',
    marketCap: '0',
    vttnPrice: 0,
    priceChange: 0,
    totalSupply: '0',
    circulatingSupply: '0',
    energyStations: 0,
    bssConnected: 0,
    subnets: 0,
    aiAgents: 0
  });

  const [latestBlocks, setLatestBlocks] = useState<Block[]>([]);
  const [latestTransactions, setLatestTransactions] = useState<Transaction[]>([]);

  // 실제 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
        
        // 네트워크 통계 가져오기
        try {
          const statsResponse = await fetch(`${apiBaseUrl}/explorer/network-stats`);
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            if (statsData.success && statsData.stats) {
              setStats({
                totalTransactions: statsData.stats.totalTransactions || 0,
                tps: parseFloat(statsData.stats.tps?.toString() || '0') || 0,
                gasPrice: parseFloat(statsData.stats.gasPrice || '0') / 1e9,
                activeValidators: statsData.stats.activeValidators || 0,
                totalStaked: statsData.stats.totalStaked || '0',
                marketCap: statsData.stats.marketCap || '0',
                vttnPrice: statsData.stats.vttnPrice || 0,
                priceChange: statsData.stats.priceChange || 0,
                totalSupply: statsData.stats.totalSupply || '0',
                circulatingSupply: statsData.stats.circulatingSupply || '0',
                energyStations: statsData.stats.energyStations || 0,
                bssConnected: statsData.stats.bssConnected || 0,
                subnets: statsData.stats.subnets || 0,
                aiAgents: statsData.stats.aiAgents || 0,
                latestBlock: statsData.stats.currentBlock || 0,
                blockTime: statsData.stats.blockTime || 0
              });
            }
          }
        } catch (statsError) {
          console.warn('통계 데이터 가져오기 실패:', statsError);
        }
        
        // 블록 데이터 가져오기
        const blocksResponse = await fetch(`${apiBaseUrl}/explorer/blocks?limit=3`);
        if (blocksResponse.ok) {
          const blocksData = await blocksResponse.json();
          if (blocksData.success && blocksData.blocks) {
            setLatestBlocks(blocksData.blocks.slice(0, 3).map((block: any) => ({
              number: block.number || 0,
              hash: block.hash || '0x0',
              timestamp: block.timestamp ? (typeof block.timestamp === 'number' ? new Date(block.timestamp * 1000).toISOString() : new Date(block.timestamp).toISOString()) : new Date().toISOString(),
              transactions: block.transactionCount || block.transactions?.length || 0,
              gasUsed: parseInt(block.gasUsed || '0'),
              gasLimit: parseInt(block.gasLimit || '0'),
              validator: block.miner || block.validator || 'Validator',
              reward: '2.0 VTTN'
            })));
          }
        }

        // 트랜잭션 데이터 가져오기
        const txResponse = await fetch(`${apiBaseUrl}/explorer/transactions?limit=3`);
        if (txResponse.ok) {
          const txData = await txResponse.json();
          if (txData.success && txData.transactions) {
            setLatestTransactions(txData.transactions.slice(0, 3).map((tx: any) => {
              let txType: 'transfer' | 'energy' | 'staking' | 'bridge' = 'transfer';
              if (!tx.to) {
                txType = 'transfer';
              } else {
                const value = parseFloat(tx.value || '0');
                if (value >= 1000) {
                  txType = 'staking';
                } else {
                  txType = 'transfer';
                }
              }

              return {
                hash: tx.hash,
                from: tx.from || '0x0000...0000',
                to: tx.to || 'Contract',
                value: `${parseFloat(tx.value || '0').toFixed(2)} VTTN`,
                gasPrice: parseFloat(tx.gasPrice || '0') / 1e9,
                timestamp: tx.timestamp ? (typeof tx.timestamp === 'number' ? new Date(tx.timestamp * 1000).toISOString() : new Date(tx.timestamp).toISOString()) : new Date().toISOString(),
                status: tx.status === 'success' ? 'success' : 'failed',
                type: txType
              };
            }));
          }
        }
      } catch (error) {
        console.error('데이터 가져오기 실패:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // 10초마다 업데이트
    return () => clearInterval(interval);
  }, []);

  const statCards = [
    {
      title: 'Total Transactions',
      value: (stats?.totalTransactions || 0).toLocaleString(),
      icon: Activity,
      color: 'blue',
      change: '+5.2%',
      link: '/transactions'
    },
    {
      title: 'Network TPS',
      value: (parseFloat(stats?.tps?.toString() || '0') || 0).toFixed(1),
      icon: Zap,
      color: 'green',
      change: '+12.8%',
      link: '/blocks'
    },
    {
      title: 'Active Validators',
      value: (stats?.activeValidators || 0).toString(),
      icon: Shield,
      color: 'purple',
      change: 'Stable',
      link: '/validators'
    },
    {
      title: 'Gas Price',
      value: `${stats?.gasPrice || 0} Gwei`,
      icon: DollarSign,
      color: 'yellow',
      change: '-2.1%',
      link: '/transactions'
    },
    {
      title: 'Energy Stations',
      value: (stats?.energyStations || 0).toString(),
      icon: Battery,
      color: 'indigo',
      change: '+3 New',
      link: '/energy'
    },
    {
      title: 'BSS Connected',
      value: (stats?.bssConnected || 0).toString(),
      icon: Database,
      color: 'pink',
      change: '+2 Today',
      link: '/bss'
    },
    {
      title: 'Total Supply',
      value: stats?.totalSupply || '0',
      icon: Cpu,
      color: 'cyan',
      change: 'Fixed',
      link: '/tokens'
    },
    {
      title: 'Circulating Supply',
      value: stats?.circulatingSupply || '0',
      icon: Globe,
      color: 'orange',
      change: '+0.5%',
      link: '/tokens'
    }
  ];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimeAgo = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'energy': return 'bg-green-100 text-green-800';
      case 'staking': return 'bg-blue-100 text-blue-800';
      case 'bridge': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-black mb-4">
            Voltron Mainnet
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            AI-Enhanced Management Network • VTTN Ecosystem
          </p>
          
          {/* Network Status */}
          <div className="flex items-center justify-center space-x-8 mb-8">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-600 font-semibold">Live Network</span>
            </div>
            <div className="flex items-center space-x-2">
              <Wifi className="w-5 h-5 text-blue-500" />
              <span className="text-blue-600 font-semibold">Chain ID: 88889</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="w-5 h-5 text-purple-500" />
              <span className="text-purple-600 font-semibold">Connected to Voltron</span>
            </div>
          </div>
        </div>

        {/* Price & Market Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-3 mb-2">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-black">${(stats?.vttnPrice || 0).toFixed(3)}</h3>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-lg ${
                  (stats?.priceChange || 0) >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                  {(stats?.priceChange || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span className="font-medium text-sm">
                    {(stats?.priceChange || 0) >= 0 ? '+' : ''}{stats?.priceChange || 0}%
                  </span>
                </div>
              </div>
              <p className="text-gray-600 font-medium">VTTN Token Price</p>
            </div>
            
            <div className="text-center">
              <h4 className="text-2xl font-bold text-black mb-1">{stats?.marketCap || '0'}</h4>
              <p className="text-gray-600 font-medium">Market Cap</p>
            </div>
            
            <div className="text-center">
              <h4 className="text-2xl font-bold text-black mb-1">{stats?.totalStaked || '0'}</h4>
              <p className="text-gray-600 font-medium">Total Staked</p>
            </div>
            
            <div className="text-center">
              <h4 className="text-2xl font-bold text-black mb-1">{stats?.circulatingSupply || '0'}</h4>
              <p className="text-gray-600 font-medium">Circulating Supply</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'bg-blue-50 text-blue-600 border-blue-200',
              green: 'bg-green-50 text-green-600 border-green-200',
              purple: 'bg-purple-50 text-purple-600 border-purple-200',
              yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
              indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
              pink: 'bg-pink-50 text-pink-600 border-pink-200',
              cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
              orange: 'bg-orange-50 text-orange-600 border-orange-200'
            };
            
            return (
              <motion.div 
                key={stat.title} 
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-105"
                onClick={() => navigate(stat.link)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${colorClasses[stat.color as keyof typeof colorClasses]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-bold px-2 py-1 rounded-lg ${
                    stat.change.startsWith('+') ? 'bg-green-100 text-green-600' : 
                    stat.change.startsWith('-') ? 'bg-red-100 text-red-600' : 
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {stat.change}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-black mb-1">{stat.value}</h3>
                <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
                <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
                  <span>상세 보기</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Latest Blocks & Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Latest Blocks */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-black">📦 Latest Blocks</h2>
              <Link 
                to="/blocks" 
                className="text-black hover:text-gray-600 font-medium flex items-center space-x-1"
              >
                <span>View All</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="space-y-4">
              {(latestBlocks || []).map((block) => (
                <div key={block.number} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <Link 
                      to={`/block/${block.number}`} 
                      className="text-black font-bold hover:text-gray-600"
                    >
                      #{block.number.toLocaleString()}
                    </Link>
                    <span className="text-gray-500 text-sm">{formatTimeAgo(block.timestamp)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Transactions:</span>
                      <span className="text-black font-medium ml-1">{block.transactions}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Validator:</span>
                      <span className="text-black font-medium ml-1">{block.validator}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Latest Transactions */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-black">💸 Latest Transactions</h2>
              <Link 
                to="/transactions" 
                className="text-black hover:text-gray-600 font-medium flex items-center space-x-1"
              >
                <span>View All</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="space-y-4">
              {(latestTransactions || []).map((tx) => (
                <div key={tx.hash} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <Link 
                      to={`/tx/${tx.hash}`} 
                      className="text-black font-bold hover:text-gray-600"
                    >
                      {formatAddress(tx.hash)}
                    </Link>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(tx.type)}`}>
                      {tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">To:</span>
                      <span className="text-black font-medium ml-1">{formatAddress(tx.to)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Value:</span>
                      <span className="text-black font-medium ml-1">{tx.value}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {formatTimeAgo(tx.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bridge Connection Info */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-black mb-2">🌉 Voltron Mainnet Ecosystem</h3>
            <p className="text-gray-600 mb-4">
              Seamlessly transfer tokens within Voltron Mainnet ecosystem
            </p>
            <div className="flex items-center justify-center space-x-4">
              <button className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium">
                Bridge Assets
              </button>
              <button className="border border-black text-black px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                View Bridge Stats
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;